import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { AliasChipsField } from './AliasChipsField';

export interface CompetitorFormValues {
  brandName: string;
  websiteUrl: string;
  aliases: string[];
}

export interface CompetitorFormInitial {
  brandName?: string;
  websiteUrl?: string;
  aliases?: string[];
}

export interface UseCompetitorFormOptions {
  active: boolean;
  initial?: CompetitorFormInitial | null;
}

export function useCompetitorForm({ active, initial }: UseCompetitorFormOptions) {
  const [brandName, setBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);

  useEffect(() => {
    if (!active) return;
    setBrandName(initial?.brandName ?? '');
    setWebsiteUrl(initial?.websiteUrl ?? '');
    setAliases(initial?.aliases ?? []);
  }, [active, initial]);

  const canSubmit = Boolean(brandName.trim() && websiteUrl.trim());
  const missingHint = !brandName.trim()
    ? 'نام برند رقیب را وارد کنید'
    : !websiteUrl.trim()
      ? 'آدرس وبسایت رقیب را وارد کنید'
      : null;

  function removeAlias(index: number) {
    setAliases((prev) => prev.filter((_, i) => i !== index));
  }

  function addAlias(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAliases((prev) => {
      const exists = prev.some(
        (alias) => alias.trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (exists) return prev;
      return [...prev, trimmed];
    });
  }

  function getValues(): CompetitorFormValues | null {
    if (!canSubmit) return null;
    return {
      brandName: brandName.trim(),
      websiteUrl: websiteUrl.trim(),
      aliases: aliases.map((a) => a.trim()).filter(Boolean),
    };
  }

  function reset(nextInitial?: CompetitorFormInitial | null) {
    setBrandName(nextInitial?.brandName ?? '');
    setWebsiteUrl(nextInitial?.websiteUrl ?? '');
    setAliases(nextInitial?.aliases ?? []);
  }

  return {
    brandName,
    setBrandName,
    websiteUrl,
    setWebsiteUrl,
    aliases,
    removeAlias,
    addAlias,
    canSubmit,
    missingHint,
    getValues,
    reset,
  };
}

export type CompetitorFormState = ReturnType<typeof useCompetitorForm>;

interface CompetitorFieldsProps {
  form: CompetitorFormState;
  formId: string;
  autoFocus?: boolean;
  /** Side-by-side name/url on wider screens */
  compact?: boolean;
  hideAliases?: boolean;
  onSubmit: () => void;
}

export function CompetitorFields({
  form,
  formId,
  autoFocus,
  compact,
  hideAliases,
  onSubmit,
}: CompetitorFieldsProps) {
  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="d-flex flex-column gap-3"
    >
      <div className={compact ? 'row g-3' : 'd-flex flex-column gap-3'}>
        <Form.Group className={compact ? 'col-md-6 mb-0' : undefined}>
          <Form.Label className="fs--1 fw-medium">نام برند رقیب</Form.Label>
          <Form.Control
            size={compact ? 'sm' : undefined}
            value={form.brandName}
            onChange={(e) => form.setBrandName(e.target.value)}
            required
            autoFocus={autoFocus}
            placeholder="مثلاً: Ahrefs"
          />
        </Form.Group>
        <Form.Group className={compact ? 'col-md-6 mb-0' : undefined}>
          <Form.Label className="fs--1 fw-medium">آدرس وبسایت رقیب</Form.Label>
          <Form.Control
            size={compact ? 'sm' : undefined}
            value={form.websiteUrl}
            onChange={(e) => form.setWebsiteUrl(e.target.value)}
            required
            placeholder="https://www.ahrefs.com"
            dir="ltr"
          />
        </Form.Group>
      </div>
      {!hideAliases && (
        <AliasChipsField
          aliases={form.aliases}
          onAdd={form.addAlias}
          onRemove={form.removeAlias}
          placeholder="مثلاً: Ahrefs SEO"
        />
      )}
    </Form>
  );
}
